import { Config } from 'config';
import { forEach, forOwn, uniq } from 'lodash';

import { Deferred, resolve } from 'gear';

import { Bounds, curvesFromSvgPath, FontData, Glyph } from 'cdl';

let { Cx } = window;

/*
{
  Info: { Id: ..., ... }
  Metrics: {
    Ascent: ...,
    Descent: ...,
    Height: ...,
    AveCharWidth: ...,
    InternalLeading: ...
  },
  Glyphs: [
    { Id: 'A', AX: 50, AY: 100, D: "svg path..." },
    { Id: 'B', AX: 45, AY: 100, D: "svg path..." },
    ...
  ],
  Kerning: { "AV": "-25", "AT": -15, ... }
}
*/

//===============================================================
let basicChars = ' '+
                'A~BCDEFGHIJKLMNOPQRSTUVWXYZ'+
                'abcdefghijklmnopqrstuvwxyz'+
                '0123456789'+
                '!"#$%&()*+,-./:;<=>?@[]^_`{|}~\\' +
                "'";
//===============================================================

export const CoreFontData = FontData.extend({ typeName: 'CoreFontData',

  init( fontInfo, requestedGlyphs, sizeRef ) {

    this._kTable = {};
    this._glyphs = {};
    this._glyphsString = "";
    this._sizeReferenceChar = sizeRef ;

    //console.log(`  CoreFontData init. sizeRef=${this._sizeReferenceChar}`);

    this.parseInfo(fontInfo,requestedGlyphs);

    const tm = fontInfo.Metrics;

    this._referenceSize = tm.RequestedSize || tm.Size;

    // Parse metrics, we leave them at the reference size to be able to share the
    // same font for multiple font sizes. The desire size is passed as a parameter when
    // asked for the metrics

    this._ascent      = tm.Ascent ;
    this._descent     = tm.Descent;
    this._height      = tm.Height ;
    this._lineSpacing = tm.LineSpacing ;

    this._width       = tm.AverageWidth;

    // See http://www.emdpi.com/fontsize.html
    // Maybe we should find a better name for this property
    this._upperSize = tm.UpperSize;

    this._spaceWidth  = tm.SpaceWidth;
    this._spaceHeight = tm.SpaceHeight;

    this.initFontData();
    
    this._metaData = fontInfo.Info.AdvancedSpacing ;

// Sample for local testing purposes    
// this._metaData = '{"50.8" : { "2" : [ { "L": "S", "R": "A", "S": 10 }, { "L": "P", "R": "L", "S": 15 }, { "S" : 10 } ], "3" : [ { "L": "M", "R": "P", "S": 20 }, { "L": "L", "R": "E", "S": 25 } , { "S" : 15 }] }}' ;
  },

  parseInfo( fontInfo, requestedGlyphs ) {
    this.parseKerning(fontInfo.Kerning);
    this.parseGlyphs(fontInfo.Glyphs,requestedGlyphs);
  },

  parseGlyphs( gc, requestedGlyphs ) {
    const _this = this, glyphs = _this._glyphs;
    forEach( gc, glyph => {
      const id = glyph.Id, aX = glyph.AX, aY = glyph.AY;
      //console.log("    GlyphID:" + id + " AX:" + aX + " AY:" + aY);
      let outlines = null, bounds;
      if( glyph.D ) {
        outlines = curvesFromSvgPath( glyph.D );
        bounds = Bounds.ofThings(outlines);
      }
      else {
        bounds = Bounds(0,0,aX,aY);
      }

      if( glyphs[id] === undefined ) {
        _this._glyphsString += id;
      }
      glyphs[id] = new Glyph(id,outlines,bounds,0,aX,aY,0);
    });

    // The server is not returning the glyph it doesn't exist.
    // We need to cache it here to avoid asking it again
    forEach( requestedGlyphs, c => {
      if( glyphs[c] === undefined ) {
        //console.log("    Glyph:" + c + " was not returned from the server!");
        glyphs[c] = null;
        _this._glyphsString += c;
      }
    });
  },

  parseKerning( kerns ) {
    const kTable = this._kTable;
    forOwn( kerns, (kern,ab) => {
      kTable[ab] = parseFloat(kern);
    });
  },

   get metaData() { return JSON.parse(this._metaData); }
});

// Load and cache full data requests from Core in memory
// A cached version with only the latin characters is loaded first, then missing
// glyphs are requested when needed.

const fontDataStore = { /* textualizedFont: { fontData, extensionQueue, extending } */ };
const temporalFontDataStore = { };
const currentRequests = {};

const processExtensions = function( entry ) {
  const queue = entry.extensionQueue, fontData = entry.data;
  if( ! entry.extending && queue.length > 0 ) {
    entry.extending = true;
    const e = queue.shift();

    //console.log(`Calling matchFullData for Glyphs:${e.GlyphsToRequest} Match:${e.Match} (for missing glyphs)`);

    Cx.Core.Font.matchFullData$({
      Match: e.Match,
      GlyphsToRequest: e.GlyphsToRequest,
      ExtraKerningGlyphs: fontData._glyphsString,
      time: Date.now() 
    }) .then( m => {
      //console.log("  matchFullData returned (for missing glyphs)");
      fontData.parseInfo(m,e.GlyphsToRequest);
      e.deferred.resolve(fontData);
      entry.extending = false;
      processExtensions(entry);
    });
  }
};

const fetchMissingGlyphs$ = function( fc, fontDataEntry, chars, sizeRef ) {

  //console.log("  Fetching missing glyphs...");

  const fontData = fontDataEntry.data;
  fontData._sizeReferenceChar = sizeRef ;
  const glyphs = fontData._glyphs;
  let missingGlyphs = '';
  forEach( uniq(chars+sizeRef), c => {
    if( c !== '\n' && c !== '\r' ) {
      if( glyphs[c] === undefined ) { // null means it doesn't exist but was already requested
        missingGlyphs += c;
      }
    }
  });
  if( missingGlyphs !== '' ) {

    //console.log(`  missingGlyphs:${missingGlyphs}`);
    
    // We need to serialize the extension of fontData, if not we could end up with missing kerning information
    const extensionDeferred = new Deferred();
    fontDataEntry.extensionQueue.push({ Match: fc, GlyphsToRequest: missingGlyphs, deferred: extensionDeferred });
    processExtensions(fontDataEntry);
    return extensionDeferred.promise;
  }
  else {
    //console.log("  no glyphs are missing");
    return resolve(fontData);
  }
};

FontData.coreFontData$ = function( font, _fc, _t, sizeRef ) {
  const fc = _fc || font.toCore(),
       t = _t  || font.textualize();

  //console.log(`  FontData.coreFontData$. Font:${t} sizeRef:${sizeRef}`);

  const alreadyCachedFontData = fontDataStore[t];
  if( alreadyCachedFontData ) {
    //console.log("    font already cached..");
    return resolve( alreadyCachedFontData );
  }

  const currentlyAskingIt = currentRequests[t];
  if( currentlyAskingIt ) {
    //console.log("    font is being currently requested..");
    return currentlyAskingIt;
  }

  //console.log("    Calling matchFullData for ALL glyphs...");
  const request = currentRequests[t] = Cx.Core.Font.matchFullData$({ Match: fc, GlyphsToRequest: basicChars+sizeRef, UseCache: Config.useCoreFontCache, time: Date.now() }) .then( m => {
    delete currentRequests[t];
    //console.log("      Creating CoreFontData with ALL glyphs...");
    const fontData = new CoreFontData(m,"",sizeRef);
    fontDataStore[t] = { data: fontData, extensionQueue: [], extending: false };
    delete temporalFontDataStore[t];
    return fontData;
  });
  return request;
};


export const loadFontDataFromCore$ = function( font, chars, sizeRef ) {

  const fc = font.toCore();
  const t = font.textualize();

  //console.log(`loadFontDataFromCore$. Font:${t} Chars:${chars} SizeRef:${sizeRef}`);

  if( ! Config.serverFontParsing ) {

    //console.log("  ... using OpenType instead of the server");
    // Direct download of the font using opentype
    let src = font.coreSrc();
    return FontData.localLoad$(font,src);
  }
  else {

    // Server font store, if serverFontParsing is false this will be empty
    const fontDataEntry = fontDataStore[t];
    if( fontDataEntry ) {
      //console.log("  ... font is in the store already");
      return fetchMissingGlyphs$(fc,fontDataEntry,chars, sizeRef);
    }

    if( Config.preReadNeededGlyphs ) {

      //console.log("  preReadNeededGlyphs configuration");

      // We request only the needed glyphs to the server to speed up rendering
      // In the background we are going to ask for the whole set of latin chars
      let neededGlyphs = '';
      forEach( uniq(chars+' MA'+sizeRef), c => {
        if( c !== '\n' && c !== '\r' ) {
          neededGlyphs += c;
        }
      });
      if( neededGlyphs === '' ) {
        // Looks like the server is returning everything if we send an empty string
        neededGlyphs = ' ';
      }

      // We save the temporal fast calls in case a figure is updated twice before the basic glyphs set is on the client
      if( ! temporalFontDataStore[t] ) {
        temporalFontDataStore[t] = {};
      }
      const tfds = temporalFontDataStore[t];
      const alreadyRequested = tfds[ neededGlyphs ];
      if( alreadyRequested ) {
        return alreadyRequested;
      }

      const promise = tfds[ neededGlyphs ] = Cx.Core.Font.matchFullData$({ Match: fc, GlyphsToRequest: neededGlyphs + sizeRef, time: Date.now() }) .then( m => {
        return new CoreFontData(m,"",sizeRef);
      });

      // Read the basic set on the background
      FontData.coreFontData$(font,fc,t,sizeRef);

      return promise;
    }
    else {
      return FontData.coreFontData$(font,fc,t,sizeRef) .then( () => {
      return fetchMissingGlyphs$(fc,fontDataStore[t],chars,sizeRef);
      });
    }
  }
};


/*
if( Config.localDebugFont ) {
  Cx.Core.Font.matchFullData$ = function() {
    return resolve(Font._varsityFontData);
  };
}
*/



/** WEBPACK FOOTER **
 ** ./src/resources/core-font-data.js
 **/